import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

interface Anexo {
  id: string;
  nome_arquivo: string;
  nome_original: string;
  tipo: string;
  tamanho: number;
  data_upload: string;
  usuario_nome: string;
}

interface AnexosAvisoProps {
  avisoId: string;
  podeAdicionar: boolean; // Só admin
  podeExcluir: boolean;   // Só admin
  onAnexosChange?: () => void;
}

const AnexosAviso: React.FC<AnexosAvisoProps> = ({
  avisoId,
  podeAdicionar,
  podeExcluir,
  onAnexosChange
}) => {
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [anexoParaExcluir, setAnexoParaExcluir] = useState<Anexo | null>(null);

  // Carregar anexos
  useEffect(() => {
    if (avisoId) {
      carregarAnexos();
    }
  }, [avisoId]);

  const carregarAnexos = async () => {
    if (!avisoId) return;
    
    setCarregando(true);
    try {
      const response = await api.get(`/aviso-anexos/aviso/${avisoId}`);
      if (response.data.success) {
        setAnexos(response.data.anexos || []);
      }
    } catch (error: any) {
      console.error('Erro ao carregar anexos:', error);
      // Não mostrar erro se for 404 (aviso sem anexos)
      if (error.response?.status !== 404) {
        toast.error('Erro ao carregar anexos');
      }
    } finally {
      setCarregando(false);
    }
  };

  // Upload de arquivo
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande! Tamanho máximo: 10MB');
      return;
    }

    // Validar tipo de arquivo
    const tiposPermitidos = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif'
    ];

    if (!tiposPermitidos.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido! Use PDF, DOC, DOCX, XLS, XLSX, PNG, JPG ou GIF');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('avisoId', avisoId);

      const response = await api.post('/aviso-anexos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Anexo adicionado com sucesso!');
        await carregarAnexos();
        if (onAnexosChange) onAnexosChange();
      }
    } catch (error: any) {
      console.error('Erro ao adicionar anexo:', error);
      toast.error(error.response?.data?.message || 'Erro ao adicionar anexo');
    } finally {
      setUploading(false);
      // Limpar input
      event.target.value = '';
    }
  };

  // Visualizar arquivo
  const handlePreview = async (anexo: Anexo) => {
    try {
      const response = await api.get(`/aviso-anexos/${anexo.id}/preview`);
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Erro ao visualizar anexo:', error);
      if (error.response?.status === 404) {
        toast.error('Arquivo não encontrado no armazenamento. Delete e faça upload novamente.');
      } else {
        toast.error('Erro ao visualizar anexo');
      }
    }
  };

  // Download de arquivo
  const handleDownload = async (anexo: Anexo) => {
    try {
      const response = await api.get(`/aviso-anexos/${anexo.id}/download`);
      
      if (response.data.success && response.data.url) {
        // Criar link temporário para forçar download
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = anexo.nome_original;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download iniciado!');
      }
    } catch (error: any) {
      console.error('Erro ao baixar anexo:', error);
      toast.error('Erro ao baixar anexo');
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (anexo: Anexo) => {
    setAnexoParaExcluir(anexo);
  };

  // Excluir anexo
  const handleDelete = async () => {
    if (!anexoParaExcluir) return;

    try {
      const response = await api.delete(`/aviso-anexos/${anexoParaExcluir.id}`);
      
      if (response.data.success) {
        toast.success('Anexo excluído com sucesso!');
        await carregarAnexos();
        if (onAnexosChange) onAnexosChange();
      }
    } catch (error: any) {
      console.error('Erro ao excluir anexo:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir anexo');
    } finally {
      setAnexoParaExcluir(null);
    }
  };

  // Obter ícone por tipo de arquivo
  const getFileIcon = (tipo: string) => {
    if (tipo.includes('pdf')) return <PdfIcon color="error" />;
    if (tipo.includes('word') || tipo.includes('document')) return <DocIcon color="primary" />;
    if (tipo.includes('image')) return <ImageIcon color="success" />;
    return <FileIcon />;
  };

  // Formatar tamanho do arquivo
  const formatarTamanho = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (carregando) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Botão de Upload (só para admin) */}
      {podeAdicionar && (
        <Box mb={2}>
          <Button
            component="label"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
            disabled={uploading}
            fullWidth
          >
            {uploading ? 'Enviando...' : 'Adicionar Anexo'}
            <input
              type="file"
              hidden
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Tamanho máximo: 10MB | Tipos: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF
          </Typography>
        </Box>
      )}

      {/* Lista de Anexos */}
      {anexos.length > 0 ? (
        <List dense>
          {anexos.map((anexo) => (
            <ListItem
              key={anexo.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <Box mr={2}>
                {getFileIcon(anexo.tipo)}
              </Box>
              <ListItemText
                primary={anexo.nome_original}
                secondary={
                  <Box component="span" display="flex" gap={1} alignItems="center">
                    <Typography variant="caption" component="span">
                      {formatarTamanho(anexo.tamanho)}
                    </Typography>
                    <Typography variant="caption" component="span">
                      •
                    </Typography>
                    <Typography variant="caption" component="span">
                      {anexo.usuario_nome}
                    </Typography>
                    <Typography variant="caption" component="span">
                      •
                    </Typography>
                    <Typography variant="caption" component="span">
                      {new Date(anexo.data_upload).toLocaleDateString('pt-BR')}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="Visualizar">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handlePreview(anexo)}
                    sx={{ mr: 1 }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Baixar">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDownload(anexo)}
                    sx={{ mr: podeExcluir ? 1 : 0 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                {podeExcluir && (
                  <Tooltip title="Excluir">
                    <IconButton
                      edge="end"
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(anexo)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <Alert severity="info" sx={{ mt: 1 }}>
          Nenhum anexo adicionado
        </Alert>
      )}

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog
        open={!!anexoParaExcluir}
        onClose={() => setAnexoParaExcluir(null)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o anexo "{anexoParaExcluir?.nome_original}"?
          </Typography>
          <Typography variant="body2" color="error" mt={1}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnexoParaExcluir(null)}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnexosAviso;
